import React, { useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import artieImage from "../../assets/images/artie.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { ArtieScoreData, Recommendation, TacticRecommendation } from "@/types/artie-score";
import { Skeleton } from "@/components/ui/skeleton";

interface ScoreStatusResponse {
  score_data: ArtieScoreData;
}

const TaskDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { projectId } = useParams<{ projectId: string }>();
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [isCommitting, setIsCommitting] = useState(false);

  const {
    data: scoreStatus,
    isLoading,
    error,
  } = useQuery<ScoreStatusResponse>({
    queryKey: ["artieScoreStatus", projectId],
    queryFn: () => {
      if (!projectId) {
        throw new Error("Project ID is missing");
      }
      return apiClient.getArtieScoreStatus(
        projectId,
      ) as Promise<ScoreStatusResponse>;
    },
    enabled: !!projectId,
  });

  // Use new V2 tactics recommendations, fallback to legacy if not available
  const currentRecommendations = scoreStatus?.score_data?.current_recommendations?.slice(0, 3) || [];
  const legacyRecommendations = scoreStatus?.score_data?.recommendations?.slice(0, 3) || [];
  const recommendations: (Recommendation | TacticRecommendation)[] = currentRecommendations.length ? currentRecommendations : legacyRecommendations;

  const [taskSelections, setTaskSelections] = useState<
    Record<
      string,
      {
        option: "self" | "assign" | null;
        collaborator?: { name: string; email: string };
      }
    >
  >({});

  const currentTask = recommendations[currentTaskIndex];
  const totalTasks = recommendations.length;

  // Helper to get current selection for the active task - handle both V2 and legacy
  const getTaskId = (task: Recommendation | TacticRecommendation, index: number) => task.id || `task-${index}`;
  const currentTaskId = currentTask ? getTaskId(currentTask, currentTaskIndex) : "";

  const currentSelection = (currentTaskId && taskSelections[currentTaskId]) || {
    option: null,
    collaborator: { name: "", email: "" },
  };

  if (!isLoading && recommendations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[oklch(1.0000_0_0)] font-sans">
        <div className="text-center p-8">
          <h2 className="text-2xl font-bold mb-4 text-[oklch(0.2686_0_0)]">No tasks available</h2>
          <p className="mb-8 text-gray-500">It looks like there are no recommendations for this project yet.</p>
          <Button
            onClick={() => navigate(`/project-v2/task-page/${projectId}`)}
            className="bg-black text-white hover:bg-black/90"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
          </Button>
        </div>
      </div>
    );
  }

  const handleOptionSelect = (option: "self" | "assign") => {
    if (!currentTaskId) return;
    setTaskSelections((prev) => ({
      ...prev,
      [currentTaskId]: {
        ...prev[currentTaskId],
        option,
        // Reset collaborator data if switching back to self, or keep it if switching to assign
        collaborator:
          option === "assign"
            ? prev[currentTaskId]?.collaborator || { name: "", email: "" }
            : undefined,
      },
    }));
  };

  const handleCollaboratorChange = (field: "name" | "email", value: string) => {
    if (!currentTaskId) return;
    setTaskSelections((prev) => ({
      ...prev,
      [currentTaskId]: {
        ...prev[currentTaskId],
        collaborator: {
          ...(prev[currentTaskId]?.collaborator || { name: "", email: "" }),
          [field]: value,
        },
      },
    }));
  };

  const handleNext = () => {
    if (currentTaskIndex < totalTasks - 1) {
      setCurrentTaskIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex((prev) => prev - 1);
    } else {
      navigate(`/project-v2/task-page/${projectId}`);
    }
  };

  // Check if the current step is "complete" enough to proceed
  const isCurrentStepComplete = () => {
    if (currentSelection.option === "self") return true;
    if (currentSelection.option === "assign") {
      return (
        currentSelection.collaborator?.name &&
        currentSelection.collaborator?.email
      );
    }
    return false;
  };

  // Check if ALL tasks are completed
  const areAllTasksComplete = () => {
    // Ensure we have exactly the number of recommendations expected (e.g. 3)
    // and that each one has a valid selection state.
    if (recommendations.length === 0) return false;

    return recommendations.every((task, index) => {
      const taskId = getTaskId(task, index);
      const selection = taskSelections[taskId];
      if (!selection || !selection.option) return false;
      if (selection.option === "assign") {
        return selection.collaborator?.name && selection.collaborator?.email;
      }
      return true;
    });
  };

  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">
      <main className="relative z-10 w-full flex flex-col items-center min-h-screen">
        <div className="w-full max-w-5xl px-6 py-12 flex flex-col items-center flex-grow">
          {/* Header & Introduction */}
          <div className="flex flex-col items-center text-center max-w-4xl mb-12">
            {/* Logo */}
            <div className="mb-8 relative">
              <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
              <img
                src={artieImage}
                alt="Artie"
                className="w-48 h-48 object-contain relative z-10"
              />
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
              Marketplace eligibility comes <br />
              with a few commitments
            </h1>

            <div className="space-y-1 text-gray-500 text-lg leading-relaxed font-ibm max-w-3xl">
              <p>
                The marketplace launching in March is where ready projects get
                introduced to real brand opportunities. To participate, we need
                to confirm you're ready to complete a small set of core
                requirements. This ensures brands are seeing projects that are
                genuinely moving toward release.
              </p>
            </div>
          </div>

          {/* Task Completion Card */}
          <div className="w-full max-w-3xl bg-white rounded-[2rem] border border-gray-100 shadow-sm relative overflow-hidden flex flex-col">
            {/* Progress Bar Segment */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gray-100"></div>
            <div
              className="absolute top-0 left-0 h-1.5 bg-black rounded-r-full transition-all duration-300"
              style={{
                width: `${((currentTaskIndex + 1) / totalTasks) * 100}%`,
              }}
            ></div>

            <div className="p-8 md:p-12 flex flex-col h-full">
              {/* Progress Indicator */}
              <div className="text-xs font-bold tracking-widest text-gray-500 mb-6 uppercase">
                TASKS {currentTaskIndex + 1} OF {totalTasks}
              </div>

              {/* Task Title & Status */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-black tracking-tight">
                  {(() => {
                    if (!currentTask) return "";
                    // Check if this is a V2 tactic recommendation or legacy recommendation
                    const isV2Tactic = 'tactic_id' in currentTask;
                    return isV2Tactic
                      ? (currentTask as TacticRecommendation).tactic_name
                      : (currentTask as Recommendation).what_to_improve;
                  })()}
                </h2>
                {/* Assuming all top 3 are required for this flow */}
                <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-3 py-1.5 rounded-full">
                  Required
                </span>
              </div>

              {/* Requirement Description Box */}
              <div className="bg-gray-50/50 rounded-lg p-4 mb-10 text-gray-600 border-l-2 border-gray-300">
                {(() => {
                  if (!currentTask) return "";
                  // Check if this is a V2 tactic recommendation or legacy recommendation
                  const isV2Tactic = 'tactic_id' in currentTask;
                  return isV2Tactic
                    ? (currentTask as TacticRecommendation).tactic_description
                    : (currentTask as Recommendation).why_it_matters;
                })()}
              </div>

              {/* Selection Options - Vertical Stack */}
              <div className="flex flex-col gap-4">
                <button
                  onClick={() => handleOptionSelect("self")}
                  className={`flex flex-col items-start p-6 rounded-xl border transition-all text-left ${currentSelection.option === "self"
                      ? "border-black bg-gray-50 ring-1 ring-black/5"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                >
                  <span className="font-bold text-gray-900 mb-1">
                    I have this covered
                  </span>
                  <span className="text-gray-500 text-sm">
                    You'll handle this deliverable yourself
                  </span>
                </button>

                <button
                  onClick={() => handleOptionSelect("assign")}
                  className={`flex flex-col items-start p-6 rounded-xl border transition-all text-left ${currentSelection.option === "assign"
                      ? "border-black bg-gray-50 ring-1 ring-black/5"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                >
                  <span className="font-bold text-gray-900 mb-1">
                    Assign someone else
                  </span>
                  <span className="text-gray-500 text-sm">
                    Invite a collaborator to handle this
                  </span>
                </button>

                {/* Expanded Form for "Assign someone else" */}
                {currentSelection.option === "assign" && (
                  <div className="mt-4 pl-1 animate-in fade-in slide-in-from-top-2 duration-200">
                    <h3 className="text-base font-semibold text-gray-700 mb-3">
                      Invite Collaborator
                    </h3>
                    <div className="space-y-4">
                      <Input
                        placeholder="Name"
                        className="h-12 text-base bg-white rounded-xl"
                        value={currentSelection.collaborator?.name || ""}
                        onChange={(e) =>
                          handleCollaboratorChange("name", e.target.value)
                        }
                      />
                      <Input
                        placeholder="Email"
                        type="email"
                        className="h-12 text-base bg-white rounded-xl"
                        value={currentSelection.collaborator?.email || ""}
                        onChange={(e) =>
                          handleCollaboratorChange("email", e.target.value)
                        }
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Next Button (Inside Card) */}
              <div className="mt-8 flex justify-end">
                {currentTaskIndex < totalTasks - 1 ? (
                  <Button
                    onClick={handleNext}
                    disabled={!isCurrentStepComplete()}
                    className="bg-black hover:bg-black/90 text-white rounded-xl px-6 h-10 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </Button>
                ) : (
                  <div className="h-10"></div> // Spacer to keep layout consistent if needed
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer & Navigation */}
        <div className="w-full mt-auto pb-10 px-6 md:px-20">
          <div className="w-full border-t border-[oklch(0.92_0_0)] mb-8" />
          <div className="w-full flex items-center justify-between">
            <div className="flex-1 flex justify-start">
              <Button
                variant="ghost"
                className="text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-transparent pl-0"
                onClick={handleBack}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>

            <div className="flex-1 flex justify-end">
              <Button
                className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!areAllTasksComplete() || isLoading || isCommitting}
                onClick={async () => {
                  if (!projectId || isCommitting) return;

                  setIsCommitting(true);
                  try {
                    for (const taskId in taskSelections) {
                      const selection = taskSelections[taskId];
                      if (
                        selection.option === "assign" &&
                        selection.collaborator?.email
                      ) {
                        try {
                          const task = recommendations.find(rec => getTaskId(rec, recommendations.indexOf(rec)) === taskId);
                          const isV2Tactic = task && 'tactic_id' in task;
                          const taskIdForInvite = task
                            ? (task as any).id || (isV2Tactic
                                ? (task as TacticRecommendation).tactic_id
                                : (task as Recommendation).what_to_improve)
                            : undefined;
                          const taskName = isV2Tactic && task
                            ? (task as TacticRecommendation).tactic_name
                            : (task as Recommendation | undefined)?.what_to_improve;
                          const taskDescription = isV2Tactic && task
                            ? (task as TacticRecommendation).tactic_description
                            : (task as Recommendation | undefined)?.why_it_matters;

                          await apiClient.addCollaborator(
                            projectId,
                            selection.collaborator.email,
                            "edit",
                            taskIdForInvite,
                            taskName,
                            taskDescription
                          );
                        } catch (err: any) {
                          console.error(`Failed to add collaborator ${selection.collaborator.email}:`, err);
                          toast({
                            title: "Failed to add collaborator",
                            description: `Could not add ${selection.collaborator.email}. verify the user exists.`,
                            variant: "destructive",
                          });
                          // Stop the process if adding a collaborator fails, so the user can fix it
                          return;
                        }

                        if (!taskId.startsWith("task-")) {
                          const task = recommendations.find(rec => getTaskId(rec, recommendations.indexOf(rec)) === taskId);
                          const isV2Tactic = task && 'tactic_id' in task;
                          
                          if (isV2Tactic) {
                            await apiClient.updateTacticRecommendation(projectId, taskId, {
                              recommendation_id: taskId,
                              status: "in_progress",
                              assigned_to: selection.collaborator.email,
                            });
                          } else {
                            await apiClient.updateRecommendation(projectId, {
                              recommendation_id: taskId,
                              status: "in_progress",
                              assigned_to: selection.collaborator.email,
                            });
                          }
                        }
                      } else if (selection.option === "self") {
                        // "I have this covered" - keep status as "generated" (stays in New tab)
                        // User will move it to in_progress when they start working on it
                      }
                    }
                    
                    // Invalidate queries to refresh Dashboard and project details with new collaborators and task statuses
                    await queryClient.invalidateQueries({ queryKey: ['projects'] });
                    await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
                    await queryClient.invalidateQueries({ queryKey: ['artieScoreStatus', projectId] });
                    
                    navigate(`/project-v2/task-done/${projectId}`, {
                      state: { taskSelections, recommendations },
                    });
                  } catch (error) {
                    console.error("Failed to commit tasks:", error);
                    toast({
                      title: "Error",
                      description: "An unexpected error occurred while saving tasks.",
                      variant: "destructive",
                    });
                  } finally {
                    setIsCommitting(false);
                  }
                }}
              >
                {isCommitting ? "Committing..." : "Commit & Continue"}
                {!isCommitting && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TaskDetailPage;
